import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  AffiliateStatusChangeData,
  ApiSuccessEnvelope,
} from "../../types/affiliates";
import { getPrivateInstance } from "../_privateInstance";

const postAffiliateLifecycle = async (
  routeTemplate: string,
  affiliateId: string,
): Promise<AffiliateStatusChangeData> => {
  const path = routeTemplate.replace(
    "{affiliate_id}",
    encodeURIComponent(affiliateId),
  );
  const response = await getPrivateInstance().post<
    ApiSuccessEnvelope<AffiliateStatusChangeData>
  >(path, {});
  return response.data.data;
};

const deactivateAffiliate = (affiliateId: string) =>
  postAffiliateLifecycle(API_ROUTES.DEACTIVATE_AFFILIATE, affiliateId);

const reactivateAffiliate = (affiliateId: string) =>
  postAffiliateLifecycle(API_ROUTES.REACTIVATE_AFFILIATE, affiliateId);

/**
 * Deactivate / reactivate an affiliate. Both endpoints flip the Cognito
 * user state.
 */
export const useDeactivateAffiliate = (): UseMutationResult<
  AffiliateStatusChangeData,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["affiliates", "deactivate"],
    mutationFn: deactivateAffiliate,
    onSuccess: (_data, affiliateId) => {
      queryClient.invalidateQueries({
        queryKey: ["affiliates", "detail", affiliateId],
      });
      queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
    },
  });
};

export const useReactivateAffiliate = (): UseMutationResult<
  AffiliateStatusChangeData,
  Error,
  string
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["affiliates", "reactivate"],
    mutationFn: reactivateAffiliate,
    onSuccess: (_data, affiliateId) => {
      queryClient.invalidateQueries({
        queryKey: ["affiliates", "detail", affiliateId],
      });
      queryClient.invalidateQueries({ queryKey: ["affiliates", "list"] });
    },
  });
};

export { deactivateAffiliate, reactivateAffiliate };
